import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {GiftShopPricingForm} from 'pages'
import {
  GiftShopUploadFormData,
  GiftShopUploadType,
  TreeSearchFototreeResponse,
  WindowModeType,
} from 'types'
import {parseDate, useDidMount, useDidUpdate} from 'utils'
import {useWindowMode} from 'windows'
import {Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopTemplateUploadFormProps} from './GiftShopTemplateUploadFormProps'
import GiftShopTemplateUploadFormPrice from './GiftShopTemplateUploadFormPrice'
import GiftShopTemplateUploadFormTags from './GiftShopTemplateUploadFormTags'
import GiftShopTemplateUploadFormDateTime from './GiftShopTemplateUploadFormDateTime'
import GiftShopTemplateUploadFormDescription from './GiftShopTemplateUploadFormDescription'
import GiftShopTemplateUploadFormLocation from './GiftShopTemplateUploadFormLocation'
import GiftShopTemplateUploadFormPeopleTag from './GiftShopTemplateUploadFormPeopleTag'

interface StyledFormProps {
  mode: WindowModeType
  uploadType?: GiftShopUploadType
}

interface StyledContainerProps {
  uploadType?: GiftShopUploadType
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${({theme, uploadType}) => ({
    backgroundColor: theme.white_1,
    paddingTop: uploadType === 'bulk' ? 0 : convertUnit(20),
    paddingLeft: uploadType === 'bulk' ? 0 : convertUnit(20),
  })}
  box-sizing: border-box;
  border-radius: ${convertUnit(8)};
  padding: 0 ${convertUnit(20)} ${convertUnit(20)};
  width: 100%;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding: 0;
    padding-bottom: ${({uploadType}) =>
      uploadType === 'video' ? convertUnit(20) : 0};
  }
`

const StyledSectionMain = styled.div<StyledFormProps>`
  display: flex;
  ${({mode}) =>
    mode === 'website' ? {flexDirection: 'row'} : {flexDirection: 'column'}}
`

const StyledFormContainer = styled.div<StyledFormProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledFormPrimary = styled(StyledFormContainer)`
  ${({mode, uploadType}) =>
    mode === 'website' &&
    uploadType === 'single' && {marginLeft: convertUnit(20)}}
  display: flex;
  justify-content: space-between;
`

const StyledFormSecondary = styled(StyledFormContainer)`
  ${({mode}) => ({
    marginLeft: mode === 'website' ? convertUnit(20) : undefined,
  })}
`

const StyledRightContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledDeleteContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin-top: ${convertUnit(20)};
  }
`

const StyledDeleteContent = styled(Paragraph)`
  align-self: left;
  cursor: pointer;
`

const StyledLocationAndFototreeContainer = styled.div`
  margin-top: ${convertUnit(25)};
  display: flex;
  gap: ${convertUnit(5)};
  flex-direction: column;
`

export default function GiftShopTemplateUploadForm({
  showDeleteButton = true,
  currencyData,
  renderImage,
  priceState,
  defaultValues,
  onFormUpdate,
  onDelete,
  onAddLocation,
  toggleModal,
  uploadType,
  hasLocationMetadata,
  latestData,
  quickFill,
  isEdit = false,
  ...props
}: GiftShopTemplateUploadFormProps) {
  const originalAt = useMemo(
    () => defaultValues.originalAt || new Date().toISOString(),
    [defaultValues],
  )
  const stateFileDate = useState(parseDate(originalAt, 'YYYY-MM-DD'))
  const stateFileTime = useState(parseDate(originalAt, 'HH:mm'))
  const defValDesc = useMemo(() => {
    if (uploadType === 'single' && !isEdit) {
      if (quickFill?.description && latestData?.description)
        return latestData.description
      return ''
    }
    return defaultValues.description
  }, [
    defaultValues.description,
    isEdit,
    latestData?.description,
    quickFill?.description,
    uploadType,
  ])
  const defValLocation = useMemo(() => {
    if (uploadType === 'single' && !isEdit) {
      if (quickFill?.location && latestData?.location)
        return JSON.stringify(latestData?.location)
      return ''
    }
    return isEdit ? defaultValues.location : ''
  }, [
    defaultValues.location,
    isEdit,
    latestData?.location,
    quickFill?.location,
    uploadType,
  ])

  const defValPrice = useMemo(() => {
    if (uploadType === 'single' && !isEdit) {
      if (quickFill?.price && latestData?.price)
        return latestData.price.toString()
      return ''
    }
    return defaultValues.price
  }, [
    defaultValues.price,
    isEdit,
    latestData?.price,
    quickFill?.price,
    uploadType,
  ])
  const stateInputPrice = useState(defValPrice || '0')
  const form = useForm<GiftShopUploadFormData>({
    defaultValues: {
      ...defaultValues,
      description: defValDesc,
      location: defValLocation,
      price: defValPrice,
    },
  })
  const {watch, register, trigger, setValue, setError, clearErrors} = form
  const {translate} = useTranslation()
  const values = watch()
  const mode = useWindowMode()
  const {currencyName, location, tags} = values
  const [inputPrice, setInputPrice] = stateInputPrice
  const stateSearchTag = useState(tags[0] ? tags[0].name : '')
  const [searchTag, setSearchTag] = stateSearchTag
  const stateSearchQuery = useState(searchTag)

  const handleValidateTagsAndLocation = useCallback(
    ({
      newLocation,
      newTags,
    }: {
      newLocation?: string
      newTags?: TreeSearchFototreeResponse[]
    }) => {
      const validatedLocation = newLocation ?? location
      const validatedTags = newTags ?? tags
      const isValid = validatedLocation.length > 0 || validatedTags.length > 0

      if (isValid) {
        clearErrors('location')
      } else {
        setError('location', {
          message: translate(
            'giftShop:uploadNeedLocationOrFototreeErrorMessage',
          ),
        })
      }
      return isValid
    },
    [clearErrors, location, setError, tags, translate],
  )

  const handleClearTagsAndLocation = useCallback(() => {
    setValue('location', '')
    setValue('tags', [])
    setSearchTag('')
  }, [setSearchTag, setValue])

  const handleRenderPriceForm = useMemo(
    () => (
      <GiftShopTemplateUploadFormPrice
        stateInputPrice={stateInputPrice}
        currencyName={currencyName}
        form={form}
        currencyData={currencyData}
      />
    ),
    [currencyData, currencyName, form, stateInputPrice],
  )

  const handleRenderTagsAndLocationForm = useMemo(
    () => (
      <StyledLocationAndFototreeContainer>
        <Paragraph fontSize="m" fontWeight="medium">
          {translate('giftShop:uploadInputLocationOrTagLabel')}
        </Paragraph>
        <GiftShopTemplateUploadFormTags
          form={form}
          tags={tags}
          uploadType={uploadType}
          hasLocationMetadata={hasLocationMetadata}
          handleValidateTagsAndLocation={handleValidateTagsAndLocation}
          handleClearTagsAndLocation={handleClearTagsAndLocation}
          stateSearchTag={stateSearchTag}
          stateSearchQuery={stateSearchQuery}
        />
        <GiftShopTemplateUploadFormLocation
          form={form}
          location={location}
          onAddLocation={onAddLocation}
          toggleModal={toggleModal}
          handleValidateTagsAndLocation={handleValidateTagsAndLocation}
          handleClearTagsAndLocation={handleClearTagsAndLocation}
        />
        {!!tags[0] && tags[0].host_fee ? (
          <Paragraph color="gray_5">
            {translate('giftShop:uploadTagHostFee', {
              hostFee: tags[0].host_fee,
            })}
          </Paragraph>
        ) : (
          <></>
        )}
      </StyledLocationAndFototreeContainer>
    ),
    [
      form,
      handleClearTagsAndLocation,
      handleValidateTagsAndLocation,
      hasLocationMetadata,
      location,
      onAddLocation,
      stateSearchQuery,
      stateSearchTag,
      tags,
      toggleModal,
      translate,
      uploadType,
    ],
  )

  const handleRenderPeopleTag = useMemo(
    () => (
      <GiftShopTemplateUploadFormPeopleTag
        form={form}
        uploadType={uploadType}
      />
    ),
    [form, uploadType],
  )

  const handleRenderDateTimeForm = useMemo(
    () => (
      <GiftShopTemplateUploadFormDateTime
        form={form}
        stateFileDate={stateFileDate}
        stateFileTime={stateFileTime}
      />
    ),
    [form, stateFileDate, stateFileTime],
  )

  const handleRenderDescriptionForm = useMemo(
    () => <GiftShopTemplateUploadFormDescription form={form} />,
    [form],
  )

  const handlePricingForm = useMemo(
    () => (
      <GiftShopPricingForm
        price={parseInt(inputPrice, 10)}
        priceState={priceState}
        tagId={tags && tags[0] ? tags[0].tag_id : undefined}
      />
    ),
    [inputPrice, priceState, tags],
  )

  const handleRenderFormPrimary = useMemo(
    () => (
      <StyledFormPrimary mode={mode} uploadType={uploadType}>
        {handleRenderPriceForm}
        {handlePricingForm}
        {handleRenderPeopleTag}
      </StyledFormPrimary>
    ),
    [
      handlePricingForm,
      handleRenderPeopleTag,
      handleRenderPriceForm,
      mode,
      uploadType,
    ],
  )

  const handleRenderFormSecondary = useMemo(
    () => (
      <StyledFormSecondary mode={mode}>
        {handleRenderTagsAndLocationForm}
        {handleRenderDateTimeForm}
        {handleRenderDescriptionForm}
      </StyledFormSecondary>
    ),
    [
      mode,
      handleRenderTagsAndLocationForm,
      handleRenderDateTimeForm,
      handleRenderDescriptionForm,
    ],
  )

  const handleRenderDeleteButton = useMemo(
    () =>
      showDeleteButton && (
        <StyledDeleteContainer>
          <StyledDeleteContent
            color="gray_3"
            fontSize="s"
            fontWeight="medium"
            onClick={onDelete}>
            {translate('giftShop:uploadDeleteContent')}
          </StyledDeleteContent>
        </StyledDeleteContainer>
      ),
    [showDeleteButton, onDelete, translate],
  )

  useDidMount(() => {
    register('content')
    register('currencyId')
    register('currencyName')
    register('extension')
    register('file')
    register('height')
    register('id')
    register('location')
    register('originalAt')
    register('peopleTag')
    register('size')
    register('tags')
    register('width')
  })

  useDidUpdate(() => {
    if (!latestData) return
    if (uploadType === 'single' && !isEdit) {
      if (quickFill?.price) {
        setValue('price', latestData.price.toString())
        setInputPrice(latestData.price.toString())
      } else setValue('price', '')
      quickFill?.location
        ? setValue('location', JSON.stringify(latestData.location))
        : setValue('location', '')
      quickFill?.description
        ? setValue('description', latestData.description)
        : setValue('description', '')
    }
  }, [quickFill, latestData, setValue])

  useEffect(() => {
    onFormUpdate && onFormUpdate(values)
  }, [values, onFormUpdate])

  useEffect(() => {
    defaultValues.location === '' && trigger('location')
  }, [values.location, defaultValues.location, trigger])

  return (
    <StyledContainer {...props} uploadType={uploadType}>
      <StyledSectionMain mode={mode}>
        {renderImage}
        <StyledRightContainer>
          <StyledSectionMain mode={mode}>
            {handleRenderFormPrimary}
            {handleRenderFormSecondary}
          </StyledSectionMain>
        </StyledRightContainer>
      </StyledSectionMain>
      {mode !== 'website' && handleRenderDeleteButton}
    </StyledContainer>
  )
}
